import { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import MyDatePicker from "../datePicker/MyDatePicker";
import UserSelection from "./UserSelection";
import EventInputForms from "./EventInputForms";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createUserEvent,
  getAllUserEvents,
  getAllUsers,
} from "../../api/users/users";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useUser } from "../../context/UserContext";

export default function AddNewEventForModalForm({ open, setOpen, refetch }) {
  const cancelButtonRef = useRef(null);
  const queryClient = useQueryClient();
  const { user, selectedYear } = useUser();
  const { data } = useQuery({
    queryKey: ["allUsers"],
    queryFn: () => getAllUsers(),
  });

  useEffect(() => {
    if (open && data && user?.role === "user") {
      const userData = data.find((item) => item.uid === user.uid);
      setEventData((prevState) => ({
        ...prevState,
        uid: userData?.uid,
        user: userData?.fullname,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, open]);

  const checkEmptyFields = (data) => {
    for (let key in data) {
      if (data[key] === "" || data[key] === null || data[key] === undefined) {
        return `The field ${key} is empty or invalid`;
      }
    }
    return "valid";
  };
  const [eventData, setEventData] = useState({
    contactPhone: "",
    datetime: "",
    uid: "",
    user: "",
    status: "pending",
    number: "",
    year: "",
    meetingHouse: "",
  });

  const { mutate, isPending } = useMutation({
    mutationFn: createUserEvent,
    onSuccess: async () => {
      await queryClient.cancelQueries({ queryKey: ["allUserEvents"] });
      const previousEvents = queryClient.getQueryData(["userEvents"]);
      const res = await getAllUserEvents(selectedYear);
      queryClient.setQueryData(["allUserEvents"], (old = []) => res);
      return { previousEvents };
    },
  });

  const createEvent = async () => {
    const data = {
      ...eventData,
      status: user?.role === "admin" ? "pending" : "accepted",
      datetime: dayjs(eventData.datetime).format("YYYY-MM-DDTHH:mm"),
      year: dayjs(eventData.datetime).format("YYYY"),
    };
    const check = checkEmptyFields(data);
    if (check !== "valid") {
      toast.error(check);
      return;
    }
    await mutate(data);
    setOpen(false);
    if (user?.role === "user") {
      setEventData({
        contactPhone: "",
        datetime: "",
        uid: "",
        user: "",
        status: "pending",
        number: "",
        year: "",
        meetingHouse: "",
      });
      refetch?.();
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        initialFocus={cancelButtonRef}
        onClose={() => {
          setOpen(false);
          setEventData({
            contactPhone: "",
            datetime: "",
            uid: "",
            user: "",
            status: "pending",
            number: "",
            year: "",
            meetingHouse: "",
          });
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 w-full sm:max-w-lg">
                <div className="bg-white rounded-lg px-4 pb-4 pt-3 sm:p-6 sm:pb-4">
                  <h2 className={"font-semibold text-center mb-2"}>
                    Προσθήκη ομιλίας
                  </h2>
                  <div className={"grid gap-2"}>
                    {user?.role === "admin" && (
                      <div className={"grid gap-2"}>
                        <label className="block text-sm font-medium text-gray-900">
                          Ομιλητής:
                        </label>
                        <UserSelection
                          users={data}
                          setEventData={setEventData}
                          eventDat={eventData}
                        />
                      </div>
                    )}

                    <div className={"grid gap-2"}>
                      <label className="block text-sm font-medium text-gray-900">
                        Ημερομηνία και ώρα:
                      </label>
                      <MyDatePicker
                        selected={eventData.datetime}
                        onChange={(e) =>
                          setEventData((prevState) => ({
                            ...prevState,
                            datetime: e,
                          }))
                        }
                        showMonthDropdown={true}
                        showYearDropdown={true}
                      />
                    </div>
                    <EventInputForms
                      setEventData={setEventData}
                      eventData={eventData}
                    />
                  </div>
                  <button
                    onClick={createEvent}
                    className={
                      "w-full mt-6 p-2 bg-slate-800 text-gray-100 rounded-xl"
                    }
                  >
                    {isPending ? "Δημιουργία..." : "Δημιουργία"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
